import { useCallback } from 'react'

import { DEFAULT_SHORTCUT_ITEM_SIZE } from 'modules/desktop/constants'
import { findFirstFreePosition } from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils/findFirstFreePosition'
import { findNearestFreePosition } from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils/findNearestFreePosition'
import { useDesktopStore } from 'modules/desktop/pages/MainPage/stores'

import { useNewFolderPlaceholderStore } from '../store/newFolderPlaceholderStore'

type Point = { x: number; y: number }

const clientPointToGridCell = (
  point: Point,
  cellSize: { width: number; height: number; gapX: number; gapY: number },
  offset: { x: number; y: number }
): { row: number; col: number } | null => {
  const gridEl = document.querySelector<HTMLDivElement>('[data-desktop-grid]')
  if (!gridEl || cellSize.width <= 0 || cellSize.height <= 0) return null

  const rect = gridEl.getBoundingClientRect()
  const x = point.x - rect.left + gridEl.scrollLeft - offset.x
  const y = point.y - rect.top + gridEl.scrollTop - offset.y

  return {
    col: Math.max(0, Math.floor(x / (cellSize.width + cellSize.gapX))),
    row: Math.max(0, Math.floor(y / (cellSize.height + cellSize.gapY)))
  }
}

export const useCreateFolder = () => {
  const items = useDesktopStore(state => state.items)
  const gridSize = useDesktopStore(state => state.gridSize)
  const cellSize = useDesktopStore(state => state.cellSize)
  const offset = useDesktopStore(state => state.offset)
  const openPlaceholder = useNewFolderPlaceholderStore(state => state.open)

  return useCallback(
    (clickPoint?: Point | null) => {
      const preferred =
        clickPoint && clientPointToGridCell(clickPoint, cellSize, offset)

      const fit = preferred
        ? findNearestFreePosition(
            items,
            gridSize.cols,
            gridSize.rows,
            DEFAULT_SHORTCUT_ITEM_SIZE,
            preferred
          )
        : findFirstFreePosition(
            items,
            gridSize.cols,
            gridSize.rows,
            DEFAULT_SHORTCUT_ITEM_SIZE
          )

      // No slot in viewport → place on a new row below all items.
      const position = fit ?? {
        row: items.reduce(
          (max, item) => Math.max(max, item.position.row + item.size.height),
          0
        ),
        col: 0
      }

      openPlaceholder(position)
    },
    [items, gridSize, cellSize, offset, openPlaceholder]
  )
}
